//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment from 'moment-timezone';
import { PojoSet } from 'pojo-sets';

import * as contentDetailApi from '@apis/content-detail/contentDetailApi';
import {
  ContentFilterSort,
  ContentFilterState,
  getContentDetailFilterDateRange,
} from '@apis/content-detail/contentDetailApiModels';
import { exportCommentsToEmail } from '@apis/gateway-instagram/gatewayInstagramApi';
import {
  getExportInstagramEngagementPostPerformanceToEmail,
  getInstagramEngagementPostPerformanceExport,
} from '@apis/instagram-user/instagramUserApi';
import * as instagramUserApi from '@apis/instagram-user/instagramUserApi';
import { EngagementPostType } from '@apis/instagram-user/instagramUserApiModel';
import { localDateToPst } from '@instagram-account/date-range-helper';
import {
  EngagementPostView,
  EngagementPostsTrendGraphValue,
  IEngagementPageParameters,
  IEngagementPostsAggregateStats,
  IEngagementPostsTrendStats,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS,
} from '@instagram-account/redux/engagement/posts/instagramAccountEngagementPostsModel';
import {
  getEngagementPostAggregateStats,
  getEngagementPostTrendStats,
} from '@instagram-account/redux/engagement/posts/instagramAccountEngagementPostsUtils';
import {
  getDateRange,
  getEngagementPostFilter,
  getIsFetchingExportPostPerformanceToEmail,
  getIsFetchingPostPerformanceExport,
  getSelectedLabels,
  getSortDir,
  getSortKey,
} from '@instagram-account/redux/selectors/getInstagramEngagement';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import ContentSourceType from '@models/ContentSourceType';
import Cuid from '@models/Cuid';
import IContentMedia from '@models/IContentMedia';
import { IInstagramPost } from '@models/IInstagramMediaItem';
import { ILabel } from '@models/ILabel';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { UniversalSearchSortDirection } from '@models/UniversalSearchSortDirection';
import { UniversalSearchSortKey } from '@models/UniversalSearchSortKey';
import { getPromotedPostsMetricsActions } from '@react-bridge/wrappers/ng-service-provider';
import { createThunk } from '@redux/createThunk';
import { getUserEmail } from '@redux/selectors/getSessionInfo';
import { createAction } from '@utils/actions';
import { FetchActionCreators, makeFetchActions } from '@utils/makeFetchActions';

import { ReduxDispatch, ReduxGetState } from 'app/curalate.store';

const setDateRange = (dateRange: IUnisonDateRange) => {
  return createAction({
    type: INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_DATE_RANGE,
    dateRange,
  });
};

const setPostType = (postType: EngagementPostType) => {
  return createAction({
    type: INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_POST_TYPE,
    postType,
  });
};

const setSelectedLabels = (labels: PojoSet<string>) => {
  return createAction({
    type: INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_SELECTED_LABELS,
    labels,
  });
};

const setSelectedTrendGraphValue = (
  selectedTrendGraphValue: EngagementPostsTrendGraphValue,
) => {
  return createAction({
    type: INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_SELECTED_TREND_GRAPH_VALUE,
    selectedTrendGraphValue,
  });
};

const setSelectedPostViewType = (postViewType: EngagementPostView) => {
  return createAction({
    type: INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_POST_VIEW_TYPE,
    postViewType,
  });
};

const setPageParameters = (params: IEngagementPageParameters) =>
  createAction({
    type: INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.SET_PAGE_PARAMETERS,
    params,
  });

const getLabels = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_LABELS,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_LABELS_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_LABELS_FAILED,
  (labels: ILabel[]) => ({ labels }),
);

const getBrandInstagramPosts = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_POSTS,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POSTS_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POSTS_FAILED,
  (data: { posts: IInstagramPost[]; totalPostCount: number }) => data,
);

const getBrandInstagramPostsStats = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_POSTS_STATS,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POSTS_STATS_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POSTS_STATS_FAILED,
  (data: {
    aggregateStats: IEngagementPostsAggregateStats;
    aggregateComparisonStats: IEngagementPostsAggregateStats;
    trendStats: IEngagementPostsTrendStats;
  }) => data,
);

const getRecentBrandInstagramPosts = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_RECENT_POSTS,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_RECENT_POSTS_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_RECENT_POSTS_FAILED,
  (recentPosts: IInstagramPost[]) => ({ recentPosts }),
);

const getLiveBrandInstagramPosts = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_LIVE_POSTS,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_LIVE_POSTS_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_LIVE_POSTS_FAILED,
  (livePosts: IInstagramPost[]) => ({ livePosts }),
);

const getMentionedTaggedPosts = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_MENTIONED_POSTS,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_MENTIONED_POSTS_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_MENTIONED_POSTS_FAILED,
  (mentionedTaggedPosts: IContentMedia[]) => ({ mentionedTaggedPosts }),
);

const getCommentsExport = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_COMMENTS_EXPORT,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_COMMENTS_EXPORT_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_COMMENTS_EXPORT_FAILED,
);

const getExportPostPerformanceToEmail = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_EXPORT_POST_PERFORMANCE_TO_EMAIL,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_EXPORT_POST_PERFORMANCE_TO_EMAIL_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_EXPORT_POST_PERFORMANCE_TO_EMAIL_FAILED,
);

const getPostPerformanceExport = makeFetchActions(
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.REQUEST_POST_PERFORMANCE_EXPORT,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POST_PERFORMANCE_EXPORT_SUCCEEDED,
  INSTAGRAM_ACCOUNT_ENGAGEMENT_POSTS_ACTIONS.RECEIVE_POST_PERFORMANCE_EXPORT_FAILED,
);

export type InstagramAccountEngagementPostsActions = ReturnType<
  | typeof setDateRange
  | typeof setPostType
  | typeof setSelectedTrendGraphValue
  | typeof setSelectedPostViewType
  | typeof setPageParameters
  | typeof setSelectedLabels
  | FetchActionCreators<typeof getBrandInstagramPosts>
  | FetchActionCreators<typeof getBrandInstagramPostsStats>
  | FetchActionCreators<typeof getLabels>
  | FetchActionCreators<typeof getRecentBrandInstagramPosts>
  | FetchActionCreators<typeof getLiveBrandInstagramPosts>
  | FetchActionCreators<typeof getMentionedTaggedPosts>
  | FetchActionCreators<typeof getCommentsExport>
  | FetchActionCreators<typeof getExportPostPerformanceToEmail>
  | FetchActionCreators<typeof getPostPerformanceExport>
>;

export const instagramAccountEngagementPostsActions = {
  setSelectedTrendGraphValue,
  setSelectedPostViewType,
  setPageParameters,

  /**
   * We need to adjust the date range coming out of the date picker
   * to include the entirety of the start and end dates to make sure
   * we get all of the posts on those days.
   */
  setDateRange:
    (dateRange: IUnisonDateRange) => async (dispatch: ReduxDispatch) => {
      const startDateSod = moment(dateRange.startDate).startOf('day').toDate();
      const endDateEod = moment(dateRange.endDate).endOf('day').toDate();

      const adjustedDateRange = {
        ...dateRange,
        startDate: startDateSod,
        endDate: endDateEod,
      };

      dispatch(setDateRange(adjustedDateRange));
    },

  clearSelectedLabels: () => async (dispatch: ReduxDispatch) => {
    dispatch(setSelectedLabels(PojoSet.empty()));
  },

  setSelectedLabelsAndFetchPostData:
    (labels: PojoSet<string>) => async (dispatch: ReduxDispatch) => {
      dispatch(setSelectedLabels(labels));
      void dispatch(
        instagramAccountEngagementPostsActions.fetchBrandInstagramPosts(),
      );
      void dispatch(
        instagramAccountEngagementPostsActions.fetchBrandPostsStats(),
      );
    },

  refreshLabels:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const { dateRange } = getState().instagram.engagement.posts;

      const startDatePst = localDateToPst(dateRange.startDate);
      const endDatePst = localDateToPst(dateRange.endDate);

      try {
        const labels =
          await instagramUserApi.getDefaultInstagramUserPostsLabels({
            startTime: startDatePst.getTime(),
            endTime: endDatePst.getTime(),
          });

        dispatch(getLabels.succeeded(labels));
      } catch (_e) {
        dispatch(
          getLabels.failed(
            'An error occurred while fetching labels. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
      dispatch(getLabels.requested());
    },

  refreshPostsData:
    (limit: number, offset: number) => async (dispatch: ReduxDispatch) => {
      dispatch(
        setPageParameters({
          limit,
          offset,
          sortKey: UniversalSearchSortKey.PostedTimestamp,
          sortDir: UniversalSearchSortDirection.Desc,
        }),
      );
      void dispatch(
        instagramAccountEngagementPostsActions.fetchBrandInstagramPosts(),
      );
      void dispatch(
        instagramAccountEngagementPostsActions.fetchBrandPostsStats(),
      );
    },

  changeDateRangeAndFetchPostsData:
    (dateRange: IUnisonDateRange, limit: number) =>
    async (dispatch: ReduxDispatch) => {
      void dispatch(
        instagramAccountEngagementPostsActions.setDateRange(dateRange),
      );
      void dispatch(
        instagramAccountEngagementPostsActions.refreshPostsData(limit, 0),
      );
      void dispatch(instagramAccountEngagementPostsActions.refreshLabels());
    },

  changePostTypeAndFetchPostsData:
    (postType: EngagementPostType) => async (dispatch: ReduxDispatch) => {
      dispatch(setPostType(postType));

      void dispatch(
        instagramAccountEngagementPostsActions.fetchBrandInstagramPosts(),
      );
      void dispatch(
        instagramAccountEngagementPostsActions.fetchBrandPostsStats(),
      );
      void dispatch(instagramAccountEngagementPostsActions.refreshLabels());
    },

  changePageParametersAndFetchPosts:
    (params: IEngagementPageParameters) => async (dispatch: ReduxDispatch) => {
      dispatch(
        instagramAccountEngagementPostsActions.setPageParameters(params),
      );
      void dispatch(
        instagramAccountEngagementPostsActions.fetchBrandInstagramPosts(),
      );
    },

  fetchBrandInstagramPosts:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const {
        dateRange,
        limit,
        offset,
        postType,
        selectedLabels,
        sortDir,
        sortKey,
      } = getState().instagram.engagement.posts;

      dispatch(getBrandInstagramPosts.requested());

      try {
        const { items: posts, total: totalPostCount } =
          await instagramUserApi.getDefaultInstagramUserPosts({
            limit,
            offset,
            sortDirection: sortDir,
            sortKey,
            startTime: localDateToPst(dateRange.startDate).getTime(),
            endTime: localDateToPst(dateRange.endDate).getTime(),
            labels: PojoSet.toArray(selectedLabels),
            includeCopyrightedPosts: true,
            engagementPostFilter: postType,
          });

        dispatch(getBrandInstagramPosts.succeeded({ posts, totalPostCount }));
        dispatch(getPromotedPostsMetricsActions().updatePromotionStates());
      } catch (_e) {
        dispatch(
          getBrandInstagramPosts.failed(
            'An error occurred while fetching posts. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },

  fetchBrandPostsStats:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const { dateRange, postType, selectedLabels } =
        getState().instagram.engagement.posts;

      dispatch(getBrandInstagramPostsStats.requested());

      const startDatePst = localDateToPst(dateRange.startDate);
      const endDatePst = localDateToPst(dateRange.endDate);

      try {
        const { comparisonStats, histogram, stats } =
          await instagramUserApi.getDefaultInstagramUserPostsStats({
            startTime: startDatePst.getTime(),
            endTime: endDatePst.getTime(),
            labels: PojoSet.toArray(selectedLabels),
            includeCopyrightedPosts: true,
            engagementPostFilter: postType,
          });

        const aggregateStats = getEngagementPostAggregateStats(stats);
        const aggregateComparisonStats =
          getEngagementPostAggregateStats(comparisonStats);
        const trendStats = getEngagementPostTrendStats(
          startDatePst,
          endDatePst,
          histogram,
        );

        dispatch(
          getBrandInstagramPostsStats.succeeded({
            aggregateStats,
            aggregateComparisonStats,
            trendStats,
          }),
        );
        dispatch(getPromotedPostsMetricsActions().updatePromotionStates());
      } catch (_e) {
        dispatch(
          getBrandInstagramPostsStats.failed(
            'An error occurred while fetching post stats. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },

  fetchRecentBrandInstagramPosts:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const {
        flags: { isFetchingRecentPosts },
      } = getState().instagram.engagement.posts;

      if (isFetchingRecentPosts) {
        return;
      }

      dispatch(getRecentBrandInstagramPosts.requested());

      try {
        const { items: recentPosts } =
          await instagramUserApi.getDefaultInstagramUserPosts({
            includeCopyrightedPosts: true,
          });

        dispatch(getRecentBrandInstagramPosts.succeeded(recentPosts));
        dispatch(getPromotedPostsMetricsActions().updatePromotionStates());
      } catch (_e) {
        dispatch(
          getRecentBrandInstagramPosts.failed(
            'An error occurred while fetching recent posts. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },

  fetchLiveBrandInstagramPosts:
    () => async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const {
        flags: { isFetchingLivePosts },
      } = getState().instagram.engagement.posts;

      if (isFetchingLivePosts) {
        return;
      }

      dispatch(getLiveBrandInstagramPosts.requested());

      try {
        const { items: recentPosts } =
          await instagramUserApi.getDefaultInstagramUserLivePosts();
        dispatch(getLiveBrandInstagramPosts.succeeded(recentPosts));
      } catch (_e) {
        dispatch(
          getLiveBrandInstagramPosts.failed(
            'An error occurred while fetching live posts. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },

  fetchMentionedAndTaggedPosts:
    (instagramUsername: string) =>
    async (dispatch: ReduxDispatch, getState: ReduxGetState) => {
      const {
        flags: { isFetchingMentionedTaggedPosts },
      } = getState().instagram.engagement.posts;

      if (isFetchingMentionedTaggedPosts) {
        return;
      }

      dispatch(getMentionedTaggedPosts.requested());

      const dateRange = getContentDetailFilterDateRange(
        moment().subtract(90, 'd').toDate(),
        new Date(),
      );

      const params = {
        source: ContentSourceType.MENTION,
        name: instagramUsername,
        limit: 24,
        reporting: false,
        f_dr: dateRange,
        f_mentions: false,
        f_sort: ContentFilterSort.Time,
        f_state: ContentFilterState.All,
      } as const;

      try {
        const { media } = await contentDetailApi.getContent(params);
        dispatch(getMentionedTaggedPosts.succeeded(media));
      } catch (_e) {
        dispatch(
          getMentionedTaggedPosts.failed(
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          ),
        );
      }
    },

  fetchCommentsExport: createThunk(
    (cuid: Cuid, userEmail: string) => async (dispatch, getState) => {
      const {
        flags: { isFetchingCommentsExport },
      } = getState().instagram.engagement.posts;

      if (isFetchingCommentsExport) {
        return;
      }

      dispatch(getCommentsExport.requested());

      try {
        await exportCommentsToEmail(cuid);
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Exporting Comments',
            type: ToastType.SUCCESS,
            message: `This can take up to several minutes.
            The comment export will be emailed to ${userEmail} when it’s complete.`,
          }),
        );
        dispatch(getCommentsExport.succeeded());
      } catch (_e) {
        dispatch(
          getCommentsExport.failed(
            'An error occurred while exporting comments. Please try again or contact Bazaarvoice Support.',
          ),
        );
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Exporting Comments Failed',
            type: ToastType.ERROR,
            message: 'An unknown error occurred.',
          }),
        );
      }
    },
  ),

  fetchExportInstagramEngagementPostPerformanceToEmail: createThunk(
    () => async (dispatch, getState) => {
      const state = getState();
      const { endDate, startDate } = getDateRange(state);
      const engagementPostFilter = getEngagementPostFilter(state);
      const sortDirection = getSortDir(state);
      const selectedLabels = getSelectedLabels(state);
      const sortKey = getSortKey(state);
      const userEmail = getUserEmail(state);
      const isFetchingExportPostPerformanceToEmail =
        getIsFetchingExportPostPerformanceToEmail(state);

      // if a request to generate the report and email is already in progress we don't want to send another request
      if (isFetchingExportPostPerformanceToEmail) {
        return;
      }

      dispatch(getExportPostPerformanceToEmail.requested());

      try {
        await getExportInstagramEngagementPostPerformanceToEmail({
          startTime: localDateToPst(startDate).getTime(),
          endTime: localDateToPst(endDate).getTime(),
          sortKey,
          sortDirection,
          labels: PojoSet.toArray(selectedLabels),
          engagementPostFilter,
        });

        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Exported Post Performance',
            type: ToastType.SUCCESS,
            message: `${userEmail} will receive your file download. It may take several minutes to receive the email.`,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );

        dispatch(getExportPostPerformanceToEmail.succeeded());
      } catch (_e) {
        dispatch(
          getExportPostPerformanceToEmail.failed(
            'An unknown error occurred while sending your CSV file download link. Please try again or contact Bazaarvoice Support.',
          ),
        );

        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Export Error',
            type: ToastType.ERROR,
            message:
              'An unknown error occurred while generating your CSV file. Please try again or contact Bazaarvoice Support.',
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
      }
    },
  ),

  fetchInstagramEngagementPostPerformanceExport: createThunk(
    (exportId: string) => async (dispatch, getState) => {
      const state = getState();

      const isFetchingPostPerformanceExport =
        getIsFetchingPostPerformanceExport(state);

      // if a request to retrieve the csv is already in progress we don't want to send another request
      if (isFetchingPostPerformanceExport) {
        return;
      }

      dispatch(getPostPerformanceExport.requested());

      try {
        const response =
          await getInstagramEngagementPostPerformanceExport(exportId);

        dispatch(getPostPerformanceExport.succeeded());
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Downloaded Post Performance',
            type: ToastType.SUCCESS,
            message: `The post performance CSV file successfully downloaded.`,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );

        return response;
      } catch (_e) {
        dispatch(
          getPostPerformanceExport.failed(
            'An unknown error occurred while downloading your CSV file. Please try again or contact Bazaarvoice Support.',
          ),
        );
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Download Error',
            type: ToastType.ERROR,
            message:
              'An unknown error occurred while downloading your CSV file. Please try again or contact Bazaarvoice Support.',
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
      }
    },
  ),
};
